// The Playground: send questions to the Exchange, with guided lessons. A
// working page (option A) in three states: a start screen while there's no
// request, lesson or response; the request and response side by side once
// there is one, with "New request" to go back to the start; and a lesson, whose
// bar says which one it is and whose coach sits in the side panel. See Design >
// Names and definitions, "Playground", and Design > Console.

import { useEffect, useRef, useState } from "preact/hooks";
import { Button, Dialog, DockPanel, Field, Icon, PageBar, PanelToggle, Workspace } from "../../../../ui/src/index.ts";
import type { PillOption } from "../../../../ui/src/index.ts";
import { usd } from "../../domain/money.ts";
import { MAX_REASON_CHARS } from "../../domain/request.ts";
import { clearPlayground, closeReject, confirmReject, lessonView, readRequest, revert } from "../actions.ts";
import { HowItWorks, PanelHead, UseTheApi, type PanelTab } from "../panels.tsx";
import { Coach, exitLesson } from "../playground/Coach.tsx";
import { RequestPanel } from "../playground/RequestPanel.tsx";
import { ResponsePanel } from "../playground/ResponsePanel.tsx";
import { Start } from "../playground/Start.tsx";
import { usePanelOpen } from "../setup.ts";
import { useStore } from "../store.ts";
import { NotConnected } from "../ui.tsx";

function RejectDialog() {
  const rejecting = useStore((s) => s.rejecting);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (rejecting) ref.current?.focus(); }, [rejecting]);
  if (!rejecting) return null;
  const submit = () => { void confirmReject(ref.current?.value ?? ""); };
  return (
    <Dialog open onClose={closeReject} title="Reject this answer?"
      description={<>You'll be credited <span class="mono">{usd(rejecting.price)}</span> at once. The refund counts against the hub's rating.</>}
      actions={<>
        <Button onClick={closeReject}>Cancel</Button>
        <Button variant="danger" onClick={submit}>Reject and refund {usd(rejecting.price)}</Button>
      </>}>
      {/* A plain textarea in the kit's look: a component wouldn't pass the ref through. */}
      <Field id="why" label="Why? (optional)">{(attrs) => <textarea {...attrs} ref={ref} class="ui-textarea" rows={3} maxLength={MAX_REASON_CHARS} />}</Field>
    </Dialog>
  );
}

const PANEL_TABS: PillOption<PanelTab>[] = [{ value: "how", label: "How it works" }, { value: "api", label: "Use the API" }];
const LESSON_TABS: PillOption<PanelTab>[] = [{ value: "lesson", label: "Lesson" }, ...PANEL_TABS];

export function Playground() {
  const s = useStore((x) => x);
  const lv = lessonView(s);
  const { empty } = readRequest(s);
  const starting = !lv && empty && !s.response && !s.sending;
  const total = s.content.lessons.length;
  const [open, setOpen] = usePanelOpen("playground", false);
  const [tab, setTab] = useState<PanelTab>("api");
  // A lesson opens the panel on its coach; leaving it falls back to the other tabs.
  const lessonId = lv?.lesson.id ?? null;
  useEffect(() => { if (lessonId) { setOpen(true); setTab("lesson"); } }, [lessonId]);
  const shown: PanelTab = tab === "lesson" && !lv ? "how" : tab;

  const tools = lv
    ? <>
      <ol class="cs-progress" aria-label={`Lesson ${lv.lesson.number} of ${total}`}>{s.content.lessons.map((l) => <li key={l.id} data-state={l.number < lv.lesson.number ? "done" : l.id === lv.lesson.id ? "now" : undefined} />)}</ol>
      <Button variant="ghost" onClick={revert}>Restart</Button>
      <Button onClick={exitLesson}>Exit lesson</Button>
    </>
    : starting ? null
    : <><NotConnected label="Share">Share</NotConnected><Button onClick={clearPlayground}><Icon name="plus" size={16} />New request</Button></>;
  const panel = open ? <DockPanel label={lv ? "Lesson" : "Details"} head={<PanelHead tab={shown} tabs={lv ? LESSON_TABS : PANEL_TABS} onTab={setTab} onClose={() => setOpen(false)} />}>
    {shown === "lesson" && lv
      ? <Coach lesson={lv.lesson} step={lv.state.step} done={lv.state.done} mismatch={lv.state.mismatch !== null} coach={lv.coach} items={lv.items} />
      : shown === "api" ? <UseTheApi /> : <HowItWorks />}
  </DockPanel> : undefined;

  return (<>
    <PageBar crumbs={lv ? `Lesson ${lv.lesson.number} of ${total}` : undefined} title={lv ? lv.lesson.title : "Playground"}>
      {tools}
      <PanelToggle open={open} onToggle={() => setOpen(!open)} />
    </PageBar>
    <Workspace panel={panel}>
      {starting ? <Start /> : <div class="cs-playground">
        <div class="cs-bench">
          {/* A new request or lesson starts the panel fresh, so its problems wait for typing again. */}
          <RequestPanel key={lv?.lesson.id ?? "own"} />
          <div class="cs-bench__right"><ResponsePanel /></div>
        </div>
      </div>}
      <RejectDialog />
    </Workspace>
  </>);
}
